"use client"

import React, { useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Loader2, AlertCircle, RefreshCw, CheckCircle } from "lucide-react"
import { Dialog, DialogContent, DialogHeader, DialogOverlay, DialogPortal, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { useMediaQuery } from "@/hooks/use-media-query"
import { useTranslations } from "@/hooks/use-translations"
import { useReviews } from "@/hooks/use-reviews"
import { wordpressApi } from "@/services/wordpress-api"
import type { NewReviewForm } from "@/types/review"
import { ReviewCarousel } from "./review-carousel"
import { ReviewForm } from "./review-form"
import { ReviewDrawer } from "./review-drawer"
import { VerificationInfo } from "./verification-info"
import { LocaleSelector } from "./locale-selector"
import { ShadowContext } from "./user-review-shadow-root"

export default function UserReviewsModule() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [showAddReview, setShowAddReview] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [selectedReview, setSelectedReview] = useState<any>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const isMobile = useMediaQuery("(max-width: 768px)")
  const itemsPerView = isMobile ? 1 : 3
  const { t } = useTranslations()
  const productName = (typeof window !== "undefined" && window._IP21_USER_REVIEWS_CONFIG?.product) || ""
  const productKey = (typeof window !== "undefined" && window._IP21_USER_REVIEWS_CONFIG?.key) || "mock"
  const title = (typeof window !== "undefined" && window._IP21_USER_REVIEWS_CONFIG?.title) || t("userExperiences", { productName })
  const isMockMode = wordpressApi.isMock()
  const shadowRoot = React.useContext(ShadowContext)!;
  const { reviews, loading, error, refreshReviews, submitReview, markHelpful } = useReviews(productKey)
  const moduleRef = useRef<HTMLDivElement>(null)
  if (!shadowRoot) {
    console.log("ShadowRoot is not available")
    return null
  }
  const handleNextSlide = () => {
    const maxIndex = Math.max(0, reviews.length - itemsPerView)
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex))
  }

  const handlePrevSlide = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0))
  }

  const handleAddReviewClick = () => {
    setShowAddReview(true)
    setSubmitError(null)
  }

  const handleSubmitReview = async (reviewData: NewReviewForm) => {
    try {
      setSubmitError(null)
      await submitReview(reviewData)
      setShowAddReview(false)
      setShowConfirmation(true)
      setCurrentIndex(0)
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t("networkError")
      setSubmitError(errorMessage)
      throw err // Re-throw to let the form handle it
    }
  }

  const handleRetry = async () => {
    await refreshReviews()
  }

  const handleCloseConfirmation = () => {
    setShowConfirmation(false)
  }

  const handleOpenChange = (open: boolean) => {
    setShowAddReview(open)
  }

  if (loading) {
    return (
      <div
        className="w-full mx-auto py-6 sm:py-8 font-sans bg-background text-foreground"
        style={{ maxWidth: "820px" }}
      >
        <LocaleSelector />
        <div className="flex items-center justify-center py-8 sm:py-12">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="ml-2">{t("loadingReviews")}</span>
          {isMockMode && (
            <Badge variant="secondary" className="ml-2">
              🎭 Mock Mode
            </Badge>
          )}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className="w-full mx-auto py-6 sm:py-8 font-sans bg-background text-foreground"
        style={{ maxWidth: "820px" }}
      >
        <LocaleSelector />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription className="flex items-center justify-between">
            <span>
              {t("errorLoadingReviews")} {error}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="ml-2 bg-transparent bg-transparent"
              onClick={handleRetry}
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-1" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-1" />
              )}
              {t("tryAgain")}
            </Button>
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div
      ref={moduleRef}
      className="w-full mx-auto py-6 sm:py-8 bg-background text-foreground font-sans"
      style={{ maxWidth: "820px" }}
    >
      <LocaleSelector />
      {/* Mobile Header Layout */}
      {isMobile ? (
        <div className="mb-4 space-y-4">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-[1.4em] font-[600] text-primary leading-tight">{title}</h2>
              {isMockMode && (
                <Badge variant="secondary" className="text-xs flex-shrink-0">
                  🎭 Mock Mode
                </Badge>
              )}
            </div>
            <p className="text-gray-600 mt-2 text-sm leading-relaxed">
              {reviews.length > 1 && t("whatDoUsersSay", { productName, count: reviews.length })}
              {reviews.length === 1 && t("whatDoUsersSaySingular", { productName })}
            </p>
          </div>
        </div>
      ) : (
        /* Desktop Header Layout */
        <div className="flex items-center justify-between mb-8">
          <div className="flex-1 min-w-0 mr-4">
            <div className="flex items-center gap-2">
              <h2 className="text-[1.4em] font-[600] text-primary">{title}</h2>
              {isMockMode && (
                <Badge variant="secondary" className="text-xs">
                  🎭 Mock Mode
                </Badge>
              )}
            </div>
            <p className="text-gray-600 mt-1">{t("whatDoUsersSay", { productName, count: reviews.length })}</p>
          </div>
          {/* <Dialog open={showAddReview} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                size="default"
                className="text-sm bg-white border-gray-300 hover:bg-gray-50 flex-shrink-0"
                onClick={handleAddReviewClick}
              >
                <span className="text-lg mr-2">+</span>
                {t("writeReview")}
              </Button>
            </DialogTrigger>
            <DialogPortal container={moduleRef?.current || undefined
            }>
              <DialogOverlay>
                <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {t("shareExperience", { productName })}
                      {isMockMode && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          🎭 Mock Mode
                        </Badge>
                      )}
                    </DialogTitle>
                  </DialogHeader>
                  {submitError && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{submitError}</AlertDescription>
                    </Alert>
                  )}
                  <ReviewForm
                    productName={productName}
                    onSubmit={handleSubmitReview}
                    onCancel={() => {
                      setShowAddReview(false)
                      setSubmitError(null)
                    }}
                  />
                </DialogContent>
              </DialogOverlay>
            </DialogPortal>
          </Dialog> */}
        </div>
      )}

      {/* Info blokje */}
      <div>
        <div className="mb-4 md:mb-6 p-3 md:p-4 rounded-lg" style={{ backgroundColor: "#c6f68d" }}>
          <div className="flex items-center gap-3">
            <div
              className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center"
              style={{ backgroundColor: "#c6f68d" }}
            ></div>
            <p className="text-black">{t("clickOnReviewToViewFullContent")}</p>
          </div>
        </div>
      </div>
      {reviews.length === 0 ? (
        <div className="text-center py-8 sm:py-12">
          <p className="text-gray-600 mb-4 px-4">{t("noReviewsAvailable", { productName })}</p>
          <Button onClick={() => setShowAddReview(true)}>{t("beFirstToReview")}</Button>
          {isMockMode && (
            <div className="mt-4">
              <Badge variant="secondary" className="text-xs">
                🎭 Running in mock mode - no real API key found
              </Badge>
            </div>
          )}
        </div>
      ) : (
        <ReviewCarousel
          reviews={reviews}
          currentIndex={currentIndex}
          itemsPerView={itemsPerView}
          onPrevSlide={handlePrevSlide}
          onNextSlide={handleNextSlide}
          onSetIndex={setCurrentIndex}
          onCardClick={setSelectedReview}
          onMarkHelpful={markHelpful}
        />
      )}

      <VerificationInfo />

      <ReviewDrawer review={selectedReview} onClose={() => setSelectedReview(null)} onMarkHelpful={markHelpful} />
      <Dialog open={showAddReview} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <div className="flex justify-center">
            <Button
              variant="outline"
              size="default"
              className="w-full md:w-auto text-sm bg-white border-gray-300 hover:bg-gray-50 py-3"
              onClick={handleAddReviewClick}
            >
              <span className="text-lg mr-2">+</span>
              {t("writeReview")}
            </Button>
          </div>
        </DialogTrigger>
        {/* Portal INTO your shadowRoot */}
        <DialogPortal container={moduleRef?.current || undefined}>
          <DialogOverlay>
            <DialogContent className="w-full h-full sm:h-auto max-w-none max-h-none m-0 rounded-none sm:max-w-2xl sm:max-h-[95vh] sm:rounded-lg overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-left">
                  {t("shareExperience", { productName })}
                  {isMockMode && (
                    <Badge variant="secondary" className="ml-2 text-xs">
                      🎭 Mock Mode
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              {submitError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}
              <ReviewForm
                productName={productName}
                onSubmit={handleSubmitReview}
                onCancel={() => {
                  setShowAddReview(false)
                  setSubmitError(null)
                }}
              />
            </DialogContent>
          </DialogOverlay>
        </DialogPortal>
      </Dialog>
      {/* Confirmation Dialog */}
      <Dialog open={showConfirmation} onOpenChange={setShowConfirmation}>
        <DialogPortal container={shadowRoot}>
          <DialogOverlay>
            <DialogContent className="max-w-md">
              <div className="space-y-6 text-center py-8 px-4">
                <div className="flex justify-center">
                  <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-12 h-12 text-green-600" />
                  </div>
                </div>
                <div className="space-y-3">
                  <h3 className="text-[1.4em] font-[600] text-primary">{t("reviewSubmitted")}</h3>
                  <p className="text-gray-600 leading-relaxed">{t("reviewSubmittedMessage")}</p>
                </div>
                <div className="pt-4">
                  <Button onClick={handleCloseConfirmation} size="lg" className="min-w-32">
                    {t("close")}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </DialogOverlay>
        </DialogPortal>
      </Dialog>
    </div>
  )
}
